import { useState, useCallback, useRef, useEffect } from 'react';
import dataSyncApi from '../../../../api/dataSync/api';

interface LogLine {
    timestamp: string;
    text: string;
    type: 'info' | 'error' | 'progress';
}

export type DbManagerJobStatus = 'idle' | 'running' | 'done' | 'failed';

const LOG_COLOR_MAP: Record<string, string> = {
    error: '#f44747',
    progress: '#6a9955',
    info: '#c586c0'
};

export default function useDbManager() {
    const [jobStatus, setJobStatus] = useState<DbManagerJobStatus>('idle');

    // Terminal: direct DOM ref
    const terminalRef = useRef<HTMLDivElement | null>(null);
    const logCacheRef = useRef<LogLine[]>([]);

    const eventSourceRef = useRef<EventSource | null>(null);
    const jobIdRef = useRef<string | null>(null);

    const renderLogLine = useCallback((container: HTMLElement, log: LogLine) => {
        const line = document.createElement('div');
        line.style.color = LOG_COLOR_MAP[log.type] || LOG_COLOR_MAP.info;

        const ts = document.createElement('span');
        ts.style.color = '#808080';
        ts.textContent = log.timestamp;
        line.appendChild(ts);

        line.appendChild(document.createTextNode(' ' + log.text));
        container.appendChild(line);
    }, []);

    const appendLog = useCallback((log: LogLine) => {
        logCacheRef.current.push(log);
        if (logCacheRef.current.length > 2000) {
            logCacheRef.current.shift();
        }

        const container = terminalRef.current;
        if (!container) return;

        // Remove placeholder if present
        const placeholder = container.querySelector('[data-placeholder]');
        if (placeholder) placeholder.remove();

        renderLogLine(container, log);
        container.scrollTop = container.scrollHeight;
    }, [renderLogLine]);

    // Ref callback: replay cached logs when terminal DOM mounts
    const setTerminalRef = useCallback((node: HTMLDivElement | null) => {
        terminalRef.current = node;
        if (node && logCacheRef.current.length > 0) {
            node.innerHTML = '';
            for (const log of logCacheRef.current) {
                renderLogLine(node, log);
            }
            node.scrollTop = node.scrollHeight;
        }
    }, [renderLogLine]);

    const clearTerminal = useCallback(() => {
        const container = terminalRef.current;
        if (container) {
            container.innerHTML = '<span data-placeholder style="color:#666">Log output will appear here...</span>';
        }
        logCacheRef.current = [];
    }, []);

    const connectSSE = useCallback((jId: string) => {
        const streamUrl = dataSyncApi.getDbManagerStreamUrl(jId);
        const es = new EventSource(streamUrl);
        eventSourceRef.current = es;

        es.addEventListener('log', (e: MessageEvent) => {
            const data = JSON.parse(e.data) as LogLine;
            appendLog(data);
        });

        es.addEventListener('done', (e: MessageEvent) => {
            const data = JSON.parse(e.data);
            setJobStatus(data.success ? 'done' : 'failed');
            jobIdRef.current = null;
            es.close();
            eventSourceRef.current = null;
        });

        es.addEventListener('error', () => {
            setJobStatus('failed');
            jobIdRef.current = null;
            es.close();
            eventSourceRef.current = null;
        });
    }, [appendLog]);

    const runAction = useCallback(async (body: Record<string, any>) => {
        setJobStatus('running');
        clearTerminal();

        try {
            const result = await dataSyncApi.runDbManagerAction(body);
            jobIdRef.current = result.jobId;
            connectSSE(result.jobId);
        } catch (err) {
            setJobStatus('failed');
            appendLog({
                timestamp: new Date().toLocaleTimeString('en-US', { hour12: false }),
                text: `Failed to start: ${err}`,
                type: 'error'
            });
        }
    }, [connectSSE, clearTerminal, appendLog]);

    const stopAction = useCallback(async () => {
        if (jobIdRef.current) {
            await dataSyncApi.stopDbManagerJob(jobIdRef.current).catch(() => {});
        }
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
        setJobStatus('idle');
        jobIdRef.current = null;
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
        };
    }, []);

    return {
        jobStatus,
        isRunning: jobStatus === 'running',
        terminalRef: setTerminalRef,
        clearTerminal,
        runAction,
        stopAction
    };
}
